import { marketingHome } from '../domains';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  type AuthError,
} from 'firebase/auth';
import { auth } from '../firebase';
import { PageTitle } from './PageTitle';
import { Wordmark } from './Wordmark';

/**
 * Email + password sign-in for ops staff. The console intentionally
 * uses a different identity vector from the Flutter apps (phone OTP):
 *   - Phone OTP is for end-users in the field.
 *   - Staff get email/password accounts from an existing admin on the
 *     Staff page (`/admin/staff`), which also sets `users/{uid}.role`.
 *
 * Forgot-password is wired to Firebase's hosted reset-email flow so
 * we don't have to host a reset UI ourselves.
 */
export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);

  async function signIn(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (e) {
      setError(friendly(e as AuthError));
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword() {
    setError(null);
    if (!email.trim()) {
      setError('Enter your email first, then click "Forgot password".');
      return;
    }
    setBusy(true);
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setResetSent(true);
    } catch (e) {
      setError(friendly(e as AuthError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-ink px-5 text-white">
      <PageTitle>dala console</PageTitle>
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center py-12">
        <Wordmark to={marketingHome} tone="onInk" size="sm" />
        <div className="mt-3 h-1 w-8 rounded-full bg-mango" />
        <h1 className="mt-8 text-3xl leading-tight">Staff sign-in</h1>
        <p className="mt-2 text-sm font-medium text-white/55">
          Ops console for the MABAMA pilot.
        </p>

        <form className="mt-8 space-y-4" onSubmit={signIn}>
          <label className="block">
            <span className="overline !text-white/40">Email</span>
            <input
              className="input mt-2 text-ink"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@hatod.app"
              autoFocus
              required
            />
          </label>
          <label className="block">
            <span className="overline !text-white/40">Password</span>
            <input
              className="input mt-2 text-ink"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          <button className="btn-primary w-full" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div className="mt-4 flex items-center justify-between text-sm font-semibold">
          <button
            type="button"
            className="text-white/50 hover:text-white hover:underline disabled:opacity-50"
            onClick={resetPassword}
            disabled={busy}
          >
            Forgot password
          </button>
          {resetSent && <span className="text-mango">Reset email sent.</span>}
        </div>

        {error && (
          <p className="mt-4 break-words text-sm font-semibold text-terracotta">
            {error}
          </p>
        )}

        <Link
          to={marketingHome}
          className="mt-10 text-sm font-bold text-white/40 hover:text-white"
        >
          ← Back to dala
        </Link>
      </div>
    </div>
  );
}

/**
 * Map the noisy Firebase Auth error codes to short, useful messages.
 * Anything we don't recognise falls back to the raw code so a missing
 * mapping is still actionable for whoever is on call.
 */
function friendly(e: AuthError): string {
  switch (e.code) {
    case 'auth/invalid-email':
      return 'Email looks malformed.';
    case 'auth/user-disabled':
      return 'This account is disabled. Contact an admin.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Wrong email or password.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Try again in a few minutes.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection.';
    default:
      return e.message ?? e.code ?? 'Could not sign in.';
  }
}
